
"use client";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Input,
  Empty,
  Spin,
  Button,
  message,
  Image,
  Checkbox,
  Progress,
  Radio,
  DatePicker,
} from "antd";
import { SearchOutlined, SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchDoctors } from "../../../authDoctor/redux/thunks/doctorsThunks";
import { resolveDoctorAvatar } from "../../../ProfileDoctor/hooks/useDoctorInformation";
import { useDocumentSharing } from "../../hooks/usedocumentsharing";
import { useSharingExpiration } from "../../hooks/useSharingExpiration";
import { getSharedDocsStorageKey, setSharedDocsInStorage } from "../../utils/storageKeyUtils";
import { selectEffectiveUserId } from "../../../sous-profile/redux/selectors/sousProfileSelectors";
import "./ShareModal.css";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

// ── Helpers ──────────────────────────────────────────────────────────────────

const pickDoctorAnyId = (doc) => {
  if (!doc || typeof doc !== "object") return null;
  const raw    = doc?.raw    || {};
  const user   = doc?.user   || raw?.user   || {};
  const doctor = doc?.doctor || raw?.doctor || {};

  return (
    user?.id       || user?._id      || user?.userId   ||
    user?.doctorId || user?.pgId     || user?.uuid     ||
    doctor?.id     || doctor?._id    || doctor?.userId ||
    doctor?.doctorId || doctor?.medecinId || doctor?.pgId || doctor?.uuid ||
    raw?.doctorId  || raw?.userId    || raw?.medecinId ||
    raw?.pgId      || raw?.uuid      ||
    doc.id         || doc._id        || doc.userId     ||
    doc.doctorId   || doc.medecinId  || doc.pgId       ||
    doc.uuid       || doc.externalId || null
  );
};

const normalizeDoctorIdBaseString = (doctorId) => {
  if (doctorId === null || doctorId === undefined) return null;
  const normalized = String(doctorId).trim();
  if (!normalized) return null;
  const lowered = normalized.toLowerCase();
  if (lowered === "undefined" || lowered === "null" || lowered === "nan") return null;
  return normalized;
};

const normalizeDoctorIdForComparison = (doctorId) => {
  const base = normalizeDoctorIdBaseString(doctorId);
  return base === null ? null : base.replace(/^DR_/i, "").trim();
};

// Cast en entier si possible — ms-authdoctor stocke des entiers
const castDoctorIdForApi = (doctorId) => {
  return String(doctorId ?? "").replace(/^DR_/i, "").trim();
};

const toExpirationHours = (expiresAt) => {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return null;
  return Math.max(1, Math.ceil(ms / (1000 * 60 * 60)));
};

// ── ContactRow ────────────────────────────────────────────────────────────────

function ContactRow({ contact, isSelected, onToggle }) {
  const { t } = useTranslation();

  const name =
    contact.fullName ||
    contact.name ||
    `${contact.firstName || ""} ${contact.lastName || ""}`.trim() ||
    contact.email ||
    t("shareModal.fallbackDoctorName", "Doctor");

  const rawAvatar = contact.avatarUrl || contact.avatar || contact.photo;

  const avatarSrc = useMemo(() => {
    if (!rawAvatar) return undefined;
    return resolveDoctorAvatar(rawAvatar);
  }, [rawAvatar]);

  const getInitials = () => {
    const first = contact?.firstName?.[0] || name.split(" ")[0]?.[0] || "";
    const last  = contact?.lastName?.[0]  || name.split(" ").slice(1).join(" ")[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  const specialty = contact.specialty || contact.role || "";

  return (
    <div
      className={`share-contact-row ${isSelected ? "is-selected" : ""}`}
      onClick={() => onToggle(contact)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle(contact);
        }
      }}
    >
      <Checkbox
        checked={isSelected}
        onChange={(e) => { e.stopPropagation(); onToggle(contact); }}
        onClick={(e) => e.stopPropagation()}
        className="share-contact-checkbox"
      />

      <div className="share-contact-avatar">
        <div className="share-contact-avatar-img-wrap">
          {avatarSrc ? (
            <Image
              src={avatarSrc}
              width={48}
              height={48}
              preview={false}
              fallback={DEFAULT_AVATAR}
              className="share-contact-img"
              style={{ borderRadius: "50%", objectFit: "cover" }}
            />
          ) : (
            <div
              className="ch-avatar--placeholder"
              style={{
                background: "#2563eb",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontWeight: "700",
                fontSize: "18px",
                borderRadius: "50%",
                width: "48px",
                height: "48px",
              }}
            >
              {getInitials()}
            </div>
          )}
        </div>
      </div>

      <div className="share-contact-info">
        <div className="share-contact-name">{name}</div>
        <div className="share-contact-specialty">
          {specialty || t("shareModal.defaultSpecialty", "Medical Professional")}
        </div>
      </div>
    </div>
  );
}

ContactRow.propTypes = {
  contact:    PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  onToggle:   PropTypes.func.isRequired,
};

// ── ShareModal ────────────────────────────────────────────────────────────────

export default function ShareModal({
  visible,
  onClose,
  documents = [],
  onShare,
  onShareSuccess,
  patientId,
}) {
  const { t }    = useTranslation();
  const dispatch = useDispatch();
  const effectiveUserId = useSelector(selectEffectiveUserId);
  
  // ✅ IMPORTANT: Use patientId FIRST (the actual patient being shared with)
  // Fall back to authUser.id ONLY if patientId is not provided (backward compatibility)
  const currentPatientId = patientId || effectiveUserId || null;
  
  // ✅ Debug logging
  useEffect(() => {
    if (!currentPatientId) {
    }
  }, [patientId, effectiveUserId, currentPatientId]);

  // ── Doctor list (Redux) ──────────────────────────────────────────────────
  const doctorState = useSelector((state) => state.doctor);
  const doctors     = doctorState?.doctors  || [];
  const loading     = doctorState?.loading  || false;
  const error       = doctorState?.error    || null;

  useEffect(() => {
    if (visible && doctors.length === 0) {
      dispatch(fetchDoctors());
    }
  }, [dispatch, visible, doctors.length]);

  const { shareDocuments, shareLoading } = useDocumentSharing();
  const { applyExpirationToSharedDocuments, extendLoading } =
    useSharingExpiration();

  // ── Local UI state ───────────────────────────────────────────────────────
  const [search,             setSearch]             = useState("");
  const [selectedDoctors,    setSelectedDoctors]    = useState([]);
  const [shareProgress,      setShareProgress]      = useState(0);
  const [expirationMode,     setExpirationMode]     = useState("none");
  const [expirationDateTime, setExpirationDateTime] = useState(null);

  // Reset when modal opens
  useEffect(() => {
    if (visible) {
      setSelectedDoctors([]);
      setSearch("");
      setShareProgress(0);
      setExpirationMode("none");
      setExpirationDateTime(null);
    }
  }, [visible]);

  // ── Filtered doctor list ─────────────────────────────────────────────────
  const filteredDoctors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [...doctors];

    return doctors.filter((doc) => {
      const name      = (doc.fullName   || doc.name      || "").toLowerCase();
      const firstName = (doc.firstName  || "").toLowerCase();
      const lastName  = (doc.lastName   || "").toLowerCase();
      const specialty = (doc.specialty  || "").toLowerCase();
      const city      = (doc.city       || "").toLowerCase();
      const email     = (doc.email      || "").toLowerCase();

      return (
        name.includes(q)      ||
        firstName.includes(q) ||
        lastName.includes(q)  ||
        specialty.includes(q) ||
        city.includes(q)      ||
        email.includes(q)
      );
    });
  }, [doctors, search]);

  // ── Doctor selection ─────────────────────────────────────────────────────
  const handleToggleDoctor = useCallback((doctor) => {
    const id = normalizeDoctorIdForComparison(pickDoctorAnyId(doctor));
    if (!id) return;

    setSelectedDoctors((prev) => {
      const exists = prev.some(
        (d) => normalizeDoctorIdForComparison(pickDoctorAnyId(d)) === id
      );
      return exists
        ? prev.filter((d) => normalizeDoctorIdForComparison(pickDoctorAnyId(d)) !== id)
        : [...prev, doctor];
    });
  }, []);

  const isDoctorSelected = useCallback(
    (doctor) => {
      const id = normalizeDoctorIdForComparison(pickDoctorAnyId(doctor));
      if (!id) return false;
      return selectedDoctors.some(
        (d) => normalizeDoctorIdForComparison(pickDoctorAnyId(d)) === id
      );
    },
    [selectedDoctors]
  );

  // ── Share handler ────────────────────────────────────────────────────────
  const handleShare = useCallback(async () => {
    if (selectedDoctors.length === 0) {
      message.warning(t("shareModal.selectAtLeastOne", "Please select at least one doctor"));
      return;
    }

    if (expirationMode === "expires" && !expirationDateTime) {
      message.warning(t("shareModal.expirationRequired", "Please choose the expiration date and time"));
      return;
    }

    if (
      expirationMode === "expires" &&
      expirationDateTime &&
      expirationDateTime.valueOf() <= Date.now()
    ) {
      message.warning(t("shareModal.expirationMustBeFuture", "Expiration must be in the future"));
      return;
    }

    // Build doctor ID list — cast to int when possible
    const doctorIds = selectedDoctors
      .map((d) => pickDoctorAnyId(d))
      .filter(Boolean)
      .map(castDoctorIdForApi);

    const expiresInHours =
      expirationMode === "expires" && expirationDateTime
        ? toExpirationHours(expirationDateTime.toISOString())
        : null;

    setShareProgress(10);

    let result;
    try {
      result = await shareDocuments({
        documents,
        doctorIds,
        expiresInHours,
      }).unwrap();
    } catch (error) {
      const errMessage =
        typeof error === "string"
          ? error
          : t("shareModal.shareFailed", "Failed to share documents");
      message.error(errMessage);
      setShareProgress(0);
      return;
    }

    const totalShared = result?.totalShared || 0;
    const totalFailed = result?.totalFailed || 0;

    if (totalShared > 0) {
      message.success(
        t("shareModal.sharedCount", {
          defaultValue: "{{count}} document(s) shared successfully",
          count: totalShared,
        })
      );
    }
    if (totalFailed > 0) {
      message.warning(
        t("shareModal.sharePartialFailed", {
          defaultValue: "{{count}} document(s) could not be shared",
          count: totalFailed,
        })
      );
    }

    if (expirationMode === "expires" && expiresInHours != null) {
      const successfulDocumentIds = Array.from(
        new Set(
          (result?.shared || [])
            .map((item) =>
              String(item?.documentId || item?.doc?.id || item?.doc?._id || "")
            )
            .filter(Boolean)
        )
      );

      if (successfulDocumentIds.length > 0 && doctorIds.length > 0) {
        const extendResult = await applyExpirationToSharedDocuments({
          documentIds: successfulDocumentIds,
          doctorIds,
          expiresInHours,
        });

        if (extendResult.failed > 0) {
          message.warning(
            t("shareModal.extendPartialFailed", {
              defaultValue:
                "Expiration updated for {{success}} access(es), {{failed}} failed",
              success: extendResult.success,
              failed: extendResult.failed,
            })
          );
        }
      }
    }

    setShareProgress(100);

    const expirationSettings = {
      hasExpiration: expirationMode === "expires",
      expiresAt:
        expirationMode === "expires" && expirationDateTime
          ? expirationDateTime.toISOString()
          : null,
    };

    const normalizeDoctorForSharedDoc = (doctor) => {
      const rawId = pickDoctorAnyId(doctor);
      const normalizedId = castDoctorIdForApi(rawId);
      const displayName =
        doctor?.fullName ||
        doctor?.name ||
        `${doctor?.firstName || ""} ${doctor?.lastName || ""}`.trim() ||
        doctor?.email ||
        "Doctor";

      return {
        id: normalizedId,
        name: displayName,
        specialty:
          doctor?.specialty ||
          doctor?.role ||
          t("shareModal.defaultSpecialty", "Medical Professional"),
        avatarUrl: doctor?.avatarUrl || doctor?.avatar || doctor?.photo || null,
        isActive: true,
        isFromRendezvous: true,
        sharedAt: new Date().toISOString(),
      };
    };


    const persistSharedDocsForCurrentPatient = () => {
      
      if (!currentPatientId) {
        return;
      }
      if (!Array.isArray(documents) || documents.length === 0) {
        return;
      }

      const successfulDocIds = new Set(
        (result?.shared || [])
          .map((item) => {
            // Normalize to string for consistent comparison
            const docId = String(item?.documentId || item?.doc?.id || item?.doc?._id || "").trim();
            return docId;
          })
          .filter(Boolean)
      );

      const docsToPersist = documents.filter((doc) => {
        // Normalize both sides to string for comparison
        const docId = String(doc?.id || doc?._id || "").trim();
        if (successfulDocIds.size === 0) {
          return true;
        }
        const matches = successfulDocIds.has(docId);
        return matches;
      });
      

      if (docsToPersist.length === 0) return;

      const doctorShareInfos = selectedDoctors
        .map(normalizeDoctorForSharedDoc)
        .filter((d) => Boolean(d.id));

      const storageKey = getSharedDocsStorageKey(currentPatientId);
      let existing = [];
      try {
        existing = JSON.parse(localStorage.getItem(storageKey) || "[]");
      } catch {
        existing = [];
      }

      const mergedByKey = new Map();
      existing.forEach((doc) => {
        const key = String(doc.id || doc._id || doc.fileUrl || doc.pdfUrl || doc.href || doc.title || "");
        if (!key) return;
        mergedByKey.set(key, {
          ...doc,
          sharedDoctors: Array.isArray(doc.sharedDoctors) ? [...doc.sharedDoctors] : [],
        });
      });

      docsToPersist.forEach((doc) => {
        const key = String(doc.id || doc._id || doc.fileUrl || doc.pdfUrl || doc.href || doc.title || "");
        if (!key) return;

        const previous = mergedByKey.get(key) || {};
        const previousDoctors = Array.isArray(previous.sharedDoctors)
          ? previous.sharedDoctors
          : [];
        const doctorsMap = new Map(
          previousDoctors.map((d) => [String(d?.id || ""), d]).filter(([id]) => Boolean(id))
        );

        doctorShareInfos.forEach((d) => {
          doctorsMap.set(String(d.id), { ...doctorsMap.get(String(d.id)), ...d });
        });

        mergedByKey.set(key, {
          ...previous,
          ...doc,
          id: doc.id || doc._id || previous.id,
          title: doc.title || doc.nom_fichier || previous.title || "Document",
          fileUrl: doc.fileUrl || doc.pdfUrl || doc.url || previous.fileUrl || previous.pdfUrl || previous.url || null,
          pdfUrl: doc.pdfUrl || doc.fileUrl || doc.url || previous.pdfUrl || previous.fileUrl || previous.url || null,
          href: doc.href || doc.fileUrl || doc.pdfUrl || doc.url || previous.href || null,
          hasExpiration: expirationSettings.hasExpiration,
          expiresAt: expirationSettings.expiresAt,
          sharedDoctors: Array.from(doctorsMap.values()),
        });
      });

      const nextDocs = Array.from(mergedByKey.values());
      const saved = setSharedDocsInStorage(currentPatientId, nextDocs);
      window.dispatchEvent(
        new CustomEvent("shared-medical-docs-updated", {
          detail: { patientId: currentPatientId, docs: nextDocs },
        })
      );
    };

    persistSharedDocsForCurrentPatient();

    if (onShare) {
      await onShare(result.shared ?? [], selectedDoctors, expirationSettings);
    }

    if (result.totalShared > 0 && onShareSuccess) {
      onShareSuccess(result.shared ?? [], selectedDoctors, expirationSettings);
    }

    setShareProgress(0);

    if (result.totalShared > 0) {
      onClose();
    }
  }, [
    selectedDoctors,
    expirationMode,
    expirationDateTime,
    documents,
    currentPatientId,
    shareDocuments,
    applyExpirationToSharedDocuments,
    onShare,
    onShareSuccess,
    onClose,
    t,
  ]);

  const isSubmitting = shareLoading || extendLoading;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={
        <div className="share-modal-title">
          <SendOutlined />
          <span>
            {t("shareModal.title", "Share Documents")}
            {documents.length > 0 && (
              <span className="share-modal-doc-count">
                ({documents.length} {t("shareModal.documents", "document(s)")})
              </span>
            )}
          </span>
        </div>
      }
      footer={
        <div className="share-modal-footer">
          {isSubmitting && shareProgress > 0 && (
            <Progress
              percent={shareProgress}
              size="small"
              status="active"
              className="share-modal-progress"
            />
          )}

          <div className="share-modal-footer-buttons">
            <Button onClick={onClose} disabled={isSubmitting}>
              {t("shareModal.cancel", "Cancel")}
            </Button>

            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleShare}
              loading={isSubmitting}
              disabled={isSubmitting}
              className="share-modal-submit"
            >
              {isSubmitting
                ? t("shareModal.sharing", "Sharing...")
                : t("shareModal.share", "Share")}
              {!isSubmitting && selectedDoctors.length > 0 && ` (${selectedDoctors.length})`}
            </Button>
          </div>
        </div>
      }
      className="share-modal"
      width={480}
      destroyOnClose
    >
      <div className="share-modal-content">
        <Input
          placeholder={t("shareModal.searchPlaceholder", "Search doctors...")}
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="share-modal-search"
          allowClear
        />

        {selectedDoctors.length > 0 && (
          <div className="share-modal-selected-info">
            {t("shareModal.selectedCount", {
              defaultValue: "{{count}} doctor(s) selected",
              count: selectedDoctors.length,
            })}
          </div>
        )}

        <div className="share-modal-expiration">
          <div className="share-modal-expiration-label">
            {t("shareModal.expirationLabel", "Expiration")}
          </div>

          <Radio.Group
            value={expirationMode}
            onChange={(e) => {
              setExpirationMode(e.target.value);
              if (e.target.value === "none") setExpirationDateTime(null);
            }}
            className="share-modal-expiration-choice"
          >
            <Radio value="none">
              {t("shareModal.noExpiration", "No expiration")}
            </Radio>
            <Radio value="expires">
              {t("shareModal.withExpiration", "With expiration")}
            </Radio>
          </Radio.Group>

          {expirationMode === "expires" && (
            <DatePicker
              showTime
              value={expirationDateTime}
              onChange={(value) => setExpirationDateTime(value)}
              className="share-modal-expiration-picker"
              placeholder={t(
                "shareModal.expirationPlaceholder",
                "Select expiration date and time"
              )}
            />
          )}
        </div>

        <div className="share-modal-list">
          {loading ? (
            <div className="share-modal-loading">
              <Spin />
              <span>{t("shareModal.loading", "Loading doctors...")}</span>
            </div>
          ) : error ? (
            <Empty
              description={t("shareModal.error", "Failed to load doctors")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : filteredDoctors.length === 0 ? (
            <Empty
              description={
                search
                  ? t("shareModal.noResults", "No doctors found")
                  : t("shareModal.noDoctors", "No doctors available")
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            filteredDoctors.map((doctor) => (
              <ContactRow
                key={
                  normalizeDoctorIdForComparison(pickDoctorAnyId(doctor)) ||
                  doctor.email ||
                  doctor.fullName ||
                  doctor.name
                }
                contact={doctor}
                isSelected={isDoctorSelected(doctor)}
                onToggle={handleToggleDoctor}
              />
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

ShareModal.propTypes = {
  visible:        PropTypes.bool.isRequired,
  onClose:        PropTypes.func.isRequired,
  documents:      PropTypes.array,
  onShare:        PropTypes.func,
  onShareSuccess: PropTypes.func,
  patientId:      PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
